import React from 'react';
import { cx, css } from '@emotion/css';
import { Link } from 'react-router-dom';

// Import custom premium background assets
import spellbowlCardImg from '../../assets/gen_images/batchesicon.png';
import mathbowlCardImg from '../../assets/gen_images/mathematics.png';
import sciencebowlCardImg from '../../assets/gen_images/sciencebowl_card.png';
import revImg from '../../assets/gen_images/rev.png';

const getCategoryDetails = (category) => {
  const cat = (category || '').toLowerCase();
  
  if (cat.includes('spell')) {
    return {
      title: 'SPELL BOWL',
      subtitle: 'BLUEPRINT TO PERFECT SPELLING',
      description: 'Master spelling patterns, complex word structures, and rules to dominate the spelling arena.',
      image: spellbowlCardImg,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px', color: '#f16602' }}>
          <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5Z" />
          <path d="M6 6h10" />
          <path d="M6 10h10" />
          <path d="M13 14H6" />
        </svg>
      )
    };
  } else if (cat.includes('math') || cat.includes('algebra')) {
    return {
      title: 'MATHBOWL',
      subtitle: 'PROBLEM SOLVING & NUMERICAL SPEED',
      description: 'Sharpen your math intuition with challenging numerical equations and arithmetic tracks.',
      image: mathbowlCardImg,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px', color: '#f16602' }}>
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
          <path d="M3 20h18" />
        </svg>
      )
    };
  } else if (cat.includes('science')) {
    return {
      title: 'SCIENCE BOWL',
      subtitle: 'CURIOUS MINDS & SCIENTIFIC INQUIRY',
      description: 'Explore biology, chemistry, physics, and earth sciences to unlock the mysteries of nature.',
      image: sciencebowlCardImg,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px', color: '#f16602' }}>
          <path d="M6 3h12" />
          <path d="M12 3v13" />
          <path d="M12 16a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" />
          <path d="M8.5 21h7" />
        </svg>
      )
    };
  } else if (cat.includes('mistake') || cat.includes('review') || cat.includes('revision')) {
    return {
      title: 'REVISION',
      subtitle: 'PRACTICE & ERROR RECOVERY',
      description: 'Review and learn from your previous mistakes to target your weak spots and grow.',
      image: revImg,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px', color: '#f16602' }}>
          <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
        </svg>
      )
    };
  } else {
    return {
      title: category || 'PRACTICE TRACK',
      subtitle: 'EXPLORE & LEARN',
      description: 'Practice challenges designed to test and build your expertise in selected academic fields.',
      image: mathbowlCardImg,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px', color: '#f16602' }}>
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
          <path d="M3 20h18" />
        </svg>
      )
    };
  }
};

const cardStyles = css`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 400px;
  border-radius: 28px;
  overflow: hidden;
  text-decoration: none;
  box-shadow: 0px 10px 30px rgba(241, 102, 2, 0.05);
  border: 1.5px solid rgba(241, 102, 2, 0.18);
  background: linear-gradient(to bottom, #fffcf9 0%, #fff3e6 65%, #ffe6cc 100%);
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 25px 50px rgba(241, 102, 2, 0.15), 0 0 35px rgba(241, 102, 2, 0.08);
    text-decoration: none;
    border-color: rgba(241, 102, 2, 0.45);
    background: linear-gradient(to bottom, #fffcf9 0%, #fff0e0 55%, #ffd4a8 100%);

    .card-logo-container {
      border-color: #f16602;
      box-shadow: 0 15px 40px rgba(241, 102, 2, 0.2);
      transform: scale(1.05);
      
      img {
        transform: scale(1.1);
      }
    }

    .logo-glow {
      transform: scale(1.2);
      opacity: 0.8;
    }
  }

  .card-logo-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 1.5rem;
    margin-bottom: 0.8rem;
    position: relative;
  }

  .card-logo-container {
    width: 140px;
    height: 140px;
    border-radius: 32px;
    overflow: hidden;
    background: #ffffff;
    border: 2px solid rgba(241, 102, 2, 0.18);
    box-shadow: 0 10px 35px rgba(241, 102, 2, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
    z-index: 2;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      padding: 12px;
      transition: transform 0.6s cubic-bezier(0.25, 1, 0.5, 1);
    }
  }

  .logo-glow {
    position: absolute;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(241, 102, 2, 0.15) 0%, transparent 70%);
    filter: blur(8px);
    z-index: 1;
    pointer-events: none;
    transition: all 0.4s ease;
  }

  .card-icon-container {
    position: absolute;
    top: 1.2rem;
    left: 1.2rem;
    z-index: 3;
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(241, 102, 2, 0.18);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .card-badge-container {
    position: absolute;
    top: 1.2rem;
    right: 1.2rem;
    z-index: 3;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(241, 102, 2, 0.18);
    padding: 5px 12px;
    border-radius: 20px;
    font-size: 0.68rem;
    font-weight: 700;
    color: #f16602;
    letter-spacing: 0.02em;
    display: flex;
    align-items: center;
    gap: 6px;

    .badge-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: #f16602;
      box-shadow: 0 0 8px #f16602;
    }
  }

  .card-content-area {
    padding: 0 2rem 1.5rem 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .card-subtitle {
    font-size: 0.68rem;
    font-weight: 700;
    color: #f16602;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    margin-bottom: 0.4rem;
  }

  .card-title {
    font-size: 1.55rem;
    font-weight: 800;
    color: #382110;
    text-transform: uppercase;
    letter-spacing: -0.01em;
    margin: 0 0 0.5rem 0;
    line-height: 1.25;
  }

  .card-description {
    font-size: 0.84rem;
    color: #594436;
    line-height: 1.45;
    margin: 0 0 0.8rem 0;
    font-weight: 500;
  }

  .btn-primary {
    cursor: pointer;
    padding: 0.8rem;
    width: 100%;
    margin-top: auto;
    background: #f16602;
    font-size: 0.8rem;
    font-weight: 700;
    color: #ffffff;
    border: 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-radius: 9999px;
    box-shadow: 0 4px 15px rgba(241, 102, 2, 0.25);
    transition: all 0.3s ease;
    text-align: center;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(241, 102, 2, 0.35);
    }
  }
`;

function PracticeNewCard(props) {
  const { category, count, level } = props.eventcategory;
  const targetPath = category === 'MathBowl' ? '/practice-subcategory' : '/' + props.target;

  // Retrieve customized details
  const details = getCategoryDetails(category);
  const logoSrc = props.customImg || details.image;

  return (
    <Link 
      to={{ pathname: targetPath, state: { category: category, level: level, premiumlevel: props.premiumlevel } }}
      className={cx(cardStyles)}
    >
      {/* Top Left Floating Icon */}
      <div className="card-icon-container">
        {details.icon}
      </div>

      {/* Top Right Floating Badge */}
      <div className="card-badge-container">
        <span className="badge-dot" />
        <span>{count === 0 ? 'NO' : count} MODULES</span>
      </div>

      {/* Center Logo Container with glow */}
      <div className="card-logo-wrapper">
        <div className="card-logo-container">
          <img src={logoSrc} alt={category} />
        </div>
        <div className="logo-glow" />
      </div>

      {/* Main Bottom-aligned Content Container */}
      <div className="card-content-area">
        {/* Track Subtitle */}
        <div className="card-subtitle">{details.subtitle}</div>

        {/* Big Card Title */}
        <h3 className="card-title">{details.title}</h3>

        {/* Short Track Description */}
        <p className="card-description">{details.description}</p>
        
        <div className="btn-primary">Let's Practice</div>
      </div>
    </Link>
  );
}

export default PracticeNewCard;
